import { useEffect, useState } from "react";
import { MailCheck, X, Timer } from "lucide-react";
import { usePoll } from "../lib/api";
import { useData } from "../lib/DataContext";
import EmailPODialog from "./EmailPODialog";

const DISMISS_KEY = "ff-po-email-dismissed";

// Overlay prompt for draft POs whose order-by date has arrived, plus the auto-send activation banner
export default function POEmailPrompt() {
  const { data, refresh } = usePoll("/po-email/ready", 30000);
  const { refreshAll } = useData();
  const { data: s, refresh: refreshS } = usePoll("/po-email/settings", 15000);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const on = () => { refresh(); refreshS(); };
    window.addEventListener("ff-po-email-changed", on);
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => { window.removeEventListener("ff-po-email-changed", on); clearInterval(t); };
  }, [refresh, refreshS]);
  const [dismissed, setDismissed] = useState(() => JSON.parse(sessionStorage.getItem(DISMISS_KEY) || "[]"));
  const [queue, setQueue] = useState([]);
  const pending = (data?.review || []).filter((p) => !dismissed.includes(p.id));
  const cancel = () => { const ids = [...dismissed, ...pending.map((p) => p.id)]; sessionStorage.setItem(DISMISS_KEY, JSON.stringify(ids)); setDismissed(ids); };
  const next = () => { setQueue((q) => q.slice(1)); refresh(); };
  const secs = s?.auto_send && s.auto_send_active_at ? Math.max(0, Math.round((new Date(s.auto_send_active_at).getTime() - now) / 1000)) : 0;
  return (
    <>
      {secs > 0 && (
        <div data-testid="auto-send-delay-banner" className="fixed top-3 left-1/2 -translate-x-1/2 z-[60] px-5 py-3 rounded-md border-2 border-[#EF4444] bg-[#1A0B0B] text-sm text-[#FCA5A5] font-semibold flex items-center gap-3 shadow-xl">
          <Timer className="w-4 h-4" />Auto-send to suppliers starts in {Math.floor(secs / 60)}:{String(secs % 60).padStart(2, "0")}. Turn it off in Master Controls → PO Email Controls to abort.
        </div>
      )}
      {data?.auto_create && pending.length > 0 && !queue.length && (
        <div data-testid="po-email-prompt" className="fixed bottom-24 lg:bottom-6 right-6 z-[55] w-[340px] p-5 rounded-md border-2 border-[#F59E0B] bg-[#0F1623] shadow-2xl">
          <div className="flex items-start gap-3">
            <MailCheck className="w-6 h-6 text-[#F59E0B] shrink-0" />
            <div className="flex-1"><div data-testid="po-email-prompt-text" className="font-display font-extrabold text-lg">{pending.length} PO{pending.length === 1 ? "" : "s"} ready to be emailed</div>
              <div className="text-xs text-[#9CA3AF] mt-1">{pending.slice(0, 3).map((p) => `${p.po_number} · ${p.supplier}`).join(" · ")}{pending.length > 3 ? " …" : ""}</div></div>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-4">
            <button data-testid="po-email-prompt-review" onClick={() => setQueue(pending.map((p) => p.id))} className="h-11 rounded-md bg-[#F59E0B] text-[#090D16] font-bold">Review</button>
            <button data-testid="po-email-prompt-cancel" onClick={cancel} className="h-11 rounded-md border border-[#334155] font-semibold flex items-center justify-center gap-1"><X className="w-4 h-4" />Cancel</button>
          </div>
        </div>
      )}
      <EmailPODialog poId={queue[0]} open={queue.length > 0} onClose={next} onSent={() => { refresh(); refreshAll(); window.dispatchEvent(new Event("ff-po-email-changed")); }} />
    </>
  );
}
